import { Switch, Route, Redirect } from 'react-router-dom';
import { Title } from 'react-head';
import { FormattedMessage } from 'react-intl';

import useAcquireTokenSilent from './hooks/useAcquireTokenSilent';
import useCustomNavigationClient from './hooks/useCustomNavigationClient';
import useConnectionConfigSetup from './hooks/useConnectionConfigSetup';
import useFetchCommonMetaData from './hooks/useFetchCommonMetaData';

import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Connection from './pages/Connection';
import Login from './pages/Login';
import Portfolios from './pages/Portfolios';
import Load from './pages/Loads';
import Batteries from 'pages/Batteries';
import Renewables from './pages/Renewables';
import HedgesContracts from './pages/HedgesContracts';
import StudyRun from './pages/StudyRun';
import BasisList from './pages/BasisList';
import Autoform from './pages/Autoform';
import BasisConfigurator from './pages/BasisConfigurator';
import ForwardItem from './components/forwardCurve/ForwardItem';

import SplashScreen from './components/layout/SplashScreen';
import AppBar from './components/layout/AppBar';
import StatusBar from './components/layout/StatusBar';
import PrivateRoute from './components/utils/PrivateRoute';
import MainRouterOutlet from './components/utils/MainRouterOutlet';

const App = () => {
    useAcquireTokenSilent();

    // setup client-side routing for Msal
    useCustomNavigationClient();

    // validate and setup connection config in axios headers & redux store
    // and redirect based on the auth status
    const { isValidatingConnection, isConnectionValid } = useConnectionConfigSetup();

    useFetchCommonMetaData(isConnectionValid);

    const appName = 'PowerSIMM V';

    return (
        <>
            <Title>{appName}</Title>

            {isValidatingConnection ? (
                <SplashScreen />
            ) : (
                <MainRouterOutlet>
                    <AppBar />

                    <Switch>
                        <PrivateRoute exact path="/">
                            <Title>
                                <FormattedMessage id="common_dashboard" /> - {appName}
                            </Title>

                            <Dashboard />
                        </PrivateRoute>

                        <PrivateRoute exact path="/connection">
                            <Title>
                                <FormattedMessage id="common_connection" /> - {appName}
                            </Title>

                            <Connection />
                        </PrivateRoute>

                        <PrivateRoute exact path="/portfolios">
                            <Title>
                                <FormattedMessage id="portfolios_title" /> - {appName}
                            </Title>

                            <Portfolios />
                        </PrivateRoute>

                        <PrivateRoute path="/loads">
                            <Title>
                                <FormattedMessage id="load_title" /> - {appName}
                            </Title>

                            <Load />
                        </PrivateRoute>

                        <PrivateRoute path="/batteries">
                            <Title>
                                <FormattedMessage id="batteries_title" /> - {appName}
                            </Title>

                            <Batteries />
                        </PrivateRoute>

                        <PrivateRoute path="/wind">
                            <Title>
                                <FormattedMessage id="wind_title" /> - {appName}
                            </Title>

                            <Renewables type="wind" key="wind" />
                        </PrivateRoute>

                        {process.env.REACT_APP_SHOW_EXPERIMENTAL_FEATURES === 'true' && (
                            <PrivateRoute path="/solar">
                                <Title>
                                    <FormattedMessage id="solar_title" /> - {appName}
                                </Title>

                                <Renewables type="solar" key="solar" />
                            </PrivateRoute>
                        )}

                        <PrivateRoute path="/hedges-contracts">
                            <Title>
                                <FormattedMessage id="hedges_and_contracts_title" /> - {appName}
                            </Title>

                            <HedgesContracts />
                        </PrivateRoute>

                        {process.env.REACT_APP_SHOW_BASIS === 'true' && (
                            <PrivateRoute path="/basis">
                                <Title>
                                    <FormattedMessage id="basis_title" /> - {appName}
                                </Title>

                                <BasisList />
                            </PrivateRoute>
                        )}

                        {process.env.REACT_APP_SHOW_BASIS === 'true' && (
                            <PrivateRoute path="/basis-configurator">
                                <Title>
                                    <FormattedMessage id="basis_configurator_title" /> - {appName}
                                </Title>

                                <BasisConfigurator />
                            </PrivateRoute>
                        )}

                        {process.env.REACT_APP_SHOW_BASIS === 'true' && (
                            <PrivateRoute path="/forward-item/:forwardItemId">
                                <ForwardItem />
                            </PrivateRoute>
                        )}

                        <PrivateRoute exact path="/run-a-study">
                            <Title>
                                <FormattedMessage id="common_run_a_study" /> - {appName}
                            </Title>

                            <StudyRun />
                        </PrivateRoute>

                        <PrivateRoute path="/jobs">
                            <Title>
                                <FormattedMessage id="jobs_title" /> - {appName}
                            </Title>

                            <Jobs />
                        </PrivateRoute>

                        {process.env.REACT_APP_SHOW_EXPERIMENTAL_FEATURES === 'true' && (
                            <PrivateRoute path="/autoform/:autoformId">
                                <Autoform />
                            </PrivateRoute>
                        )}

                        <Route exact path="/login">
                            <Title>
                                <FormattedMessage id="login_sign_in_title" /> - {appName}
                            </Title>

                            <Login />
                        </Route>

                        <Redirect to={{ pathname: '/', search: window.location.search }} />
                    </Switch>

                    <StatusBar />
                </MainRouterOutlet>
            )}
        </>
    );
};

export default App;
